import { useState, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import { apiAcyncQuizProgress, apiCloseRoom } from "../../utils/api-routes";
import { useSingleton } from "../../hooks/use-singleton";
import { useNotify } from "../../hooks/use-notify";

export const useHandler = ({ roomCode, mode, setShowLeaderboard, testName: passedTestName }) => {
  console.log(roomCode, passedTestName, "roomcodehanldr");
  const { get, post } = useApi();
  const { mail } = useSingleton();
  const { toggleNotify } = useNotify();
  const [quizResult, setQuizResult] = useState(null);
  const [search, setSearch] = useState("");
  const isChapterName = (val) => {
    if (!val || typeof val !== "string") return false;
    const lower = val.trim().toLowerCase();
    return (
      lower.startsWith("chapter:") ||
      lower.startsWith("chapter :") ||
      lower.startsWith("chapter-") ||
      lower.startsWith("chapter ")
    );
  };

  const initialResolved = !isChapterName(passedTestName) ? passedTestName || "" : "";
  const [resolvedTestName, setResolvedTestName] = useState(initialResolved);

  const [showAddStudentsModal, setShowAddStudentsModal] = useState(false);
  const [studentMailDetails, setStudentMailDetails] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (passedTestName && !isChapterName(passedTestName)) {
      setResolvedTestName(passedTestName);
    } else if (roomCode && mail) {
      fetchTestNameByRoomCode(roomCode);
    }
  }, [passedTestName, roomCode, mail]);

  useEffect(() => {
    // if (!roomCode) return;
    if (mode !== "async" || !roomCode) return;

    // initial api call
    getQuizResult();

    // auto refresh every 2 sec
    const interval = setInterval(() => {
      getQuizResult();
    }, 2000);

    // cleanup
    return () => clearInterval(interval);
  }, [roomCode]);

  const fetchTestNameByRoomCode = async (targetRoomCode) => {
    if (!mail || !targetRoomCode) return "";
    try {
      const res = await get(
        `api/teacher/test-management/tests?teacher_email=${mail}&test_type=all&page=1&page_size=100`
      );
      const tests =
        res?.data?.tests ||
        res?.tests ||
        (Array.isArray(res?.data) ? res.data : []);
      const found = tests.find(
        (t) => String(t?.room_code || t?.roomCode) === String(targetRoomCode)
      );
      if (found && (found.test_name || found.testName)) {
        const name = found.test_name || found.testName;
        if (!isChapterName(name)) {
          setResolvedTestName(name);
          return name;
        }
      }
    } catch (err) {
      console.log("Error looking up test name by room code", err);
    }
    return "";
  };

  const getQuizResult = async () => {
    try {
      const res = await get(apiAcyncQuizProgress(roomCode));

      console.log(res, "quiz result response");

      setQuizResult(res?.data);
      // Note: Progress API returns quiz/chapter name in test_name, so we do NOT use it for test-management APIs.
    } catch (e) {
      console.log("Error on getting quiz result", e);
    }
  };

  const getStudentMailsList = async (customTestName, customRoomCode) => {
    if (!mail) return;
    const targetRoomCode =
      customRoomCode ||
      roomCode ||
      quizResult?.room_code ||
      quizResult?.roomCode ||
      "";

    // Strictly prioritize real testName from the test-management table (near room code)
    let testName = customTestName || passedTestName || resolvedTestName || "";
    if (isChapterName(testName)) {
      testName = "";
    }

    // If testName is still missing or was a chapter name, lookup by roomCode from test-management tests list
    if (!testName && targetRoomCode) {
      testName = (await fetchTestNameByRoomCode(targetRoomCode)) || "";
    }

    try {
      let url = `api/teacher/test-management/students?teacher_email=${mail}&tier=All`;
      if (testName) {
        url += `&test_name=${encodeURIComponent(testName)}`;
      }
      url += `&page=1&page_size=5000`;
      if (targetRoomCode) {
        url += `&room_code=${encodeURIComponent(targetRoomCode)}`;
      }
      const res = await get(url);
      if (res) {
        setStudentMailDetails(res?.data?.students || []);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET STUDENT MAIL LIST", e);
    }
  };

  const addStudentsToRoom = async (emails) => {
    if (!roomCode || !emails || emails.length === 0) return;
    setIsLoading(true);
    try {
      const res = await post(
        `api/teacher/test-management/${roomCode}/students`,
        { emails }
      );
      if (res) {
        const data = res?.data?.data || res?.data || {};
        const addedCount = data.added !== undefined ? Number(data.added) : emails.length;
        const totalAssigned = data.total_assigned_students !== undefined ? Number(data.total_assigned_students) : undefined;

        let description = `${addedCount} student${addedCount === 1 ? "" : "s"} added.`;
        if (totalAssigned !== undefined) {
          description += ` Total assigned students: ${totalAssigned}.`;
        }

        if (toggleNotify) {
          toggleNotify({
            type: "success",
            title: "Students Added",
            description,
          });
        }

        setShowAddStudentsModal(false);
        setSelectedStudents([]);
        await getQuizResult();
      }
    } catch (e) {
      const errorMessage =
        e?.response?.data?.detail || "An error occurred while adding students.";
      if (toggleNotify) {
        toggleNotify({
          type: "error",
          title: "Failed to Add Students",
          description: errorMessage,
        });
      }
      console.log("ERROR ADDING STUDENTS TO ROOM", e);
    } finally {
      setIsLoading(false);
    }
  };

  const closeRoom = async () => {
    try {
      const res = await post(apiCloseRoom(roomCode, mail));
      setShowLeaderboard(true);

    } catch (e) {
      console.log("Error on closing room", e);
    }
  };

  return {
    quizResult,
    getQuizResult,
    search,
    setSearch,
    closeRoom,
    showAddStudentsModal,
    setShowAddStudentsModal,
    studentMailDetails,
    getStudentMailsList,
    selectedStudents,
    setSelectedStudents,
    searchQuery,
    setSearchQuery,
    isLoading,
    addStudentsToRoom,
  };
};
